import {
  propertyMappings,
  excludedProperties,
  tooltipConditions,
  staticTooltipProperties,
} from "../global/constants/propertyMappings";
import { VOCACAO_LAYER_NAME } from "../global/constants/categoricalLegends";
import { applySitiosArqueologicosCorrections } from "./sitiosArqueologicosCorrections";
import { parseVocacoes } from "./vocacoes";

/**
 * Check if a value is empty (null, undefined, empty string, or whitespace-only)
 */
export function isEmptyValue(value: any): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "string" && value.trim() === "")
  );
}

/**
 * Get the mapped label for a property
 */
export function getPropertyLabel(
  layerName: string,
  propertyKey: string
): string {
  const layerMappings = propertyMappings[layerName];
  if (layerMappings && layerMappings[propertyKey]) {
    return layerMappings[propertyKey].label;
  }

  // Fallback: format the property key to be more readable
  return propertyKey
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Check if a property should be displayed in tooltip
 */
export function shouldDisplayProperty(
  propertyKey: string,
  value: any
): boolean {
  // Exclude null, undefined, and empty string values
  if (value === null || value === undefined || value === "") {
    return false;
  }

  // Exclude technical/internal properties - use exact match
  const lowerKey = propertyKey.toLowerCase();
  return !excludedProperties.some(
    (excluded) => lowerKey === excluded.toLowerCase()
  );
}

/**
 * Format a property value if a formatter is defined
 */
export function formatPropertyValue(
  layerName: string,
  propertyKey: string,
  value: any
): string {
  const layerMappings = propertyMappings[layerName];
  if (layerMappings && layerMappings[propertyKey]?.format) {
    try {
      return layerMappings[propertyKey].format!(value);
    } catch (error) {
      console.warn(
        `Error formatting value for ${layerName}.${propertyKey}:`,
        error
      );
      return String(value);
    }
  }

  return String(value);
}

function normalizeAplSuffix(value: unknown): string {
  const normalized = String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  const aliases: Record<string, string> = {
    confeccoes: "confeccao",
    laticinio: "leite_e_derivados",
    laticinios: "leite_e_derivados",
    sucroenergetico: "sucroenergetico",
  };

  return aliases[normalized] ?? normalized;
}

function formatInteger(value: any): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return number.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
}

function formatYear(value: any): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return String(Math.trunc(number));
}

function formatDecimal(value: any): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildAplTooltipProperty(
  properties: Record<string, any>,
  key: string,
  label: string,
  order: number,
  formatter?: (value: any) => string
): { key: string; label: string; value: string; order: number } | null {
  const value = properties[key];
  if (!shouldDisplayProperty(key, value)) return null;

  return {
    key,
    label,
    value: formatter ? formatter(value) : String(value),
    order,
  };
}

function processAplPropertiesForTooltip(
  properties: Record<string, any>
): Array<{ key: string; label: string; value: string; order: number }> {
  if (isEmptyValue(properties.apls)) return [];

  const suffix = normalizeAplSuffix(properties.apls);
  const items = [
    buildAplTooltipProperty(properties, "apls", "Arranjo Produtivo Local", 1),
    buildAplTooltipProperty(properties, "nm_mun", "Município", 2),
    buildAplTooltipProperty(properties, "rd", "RD", 3),
    buildAplTooltipProperty(properties, "ano", "Ano", 4, formatYear),
    buildAplTooltipProperty(
      properties,
      "emprego_total",
      "Emprego total",
      5,
      formatInteger
    ),
    buildAplTooltipProperty(
      properties,
      "estabelecimento_total",
      "Estabelecimentos totais",
      6,
      formatInteger
    ),
    buildAplTooltipProperty(
      properties,
      `emprego_${suffix}`,
      "Emprego no APL",
      7,
      formatInteger
    ),
    buildAplTooltipProperty(
      properties,
      `estabelecimentos_${suffix}`,
      "Estabelecimentos no APL",
      8,
      formatInteger
    ),
    buildAplTooltipProperty(
      properties,
      `ql_${suffix}`,
      "QL do APL",
      9,
      formatDecimal
    ),
    buildAplTooltipProperty(
      properties,
      `pr_${suffix}`,
      "PR do APL",
      10,
      formatDecimal
    ),
  ];

  return items.filter(
    (
      item
    ): item is { key: string; label: string; value: string; order: number } =>
      item !== null
  );
}

/**
 * Get the display order for a property
 */
export function getPropertyOrder(
  layerName: string,
  propertyKey: string
): number {
  const layerMappings = propertyMappings[layerName];
  if (layerMappings && layerMappings[propertyKey]?.order !== undefined) {
    return layerMappings[propertyKey].order!;
  }

  // Default order: unmapped properties appear last
  return 999;
}

/**
 * Process feature properties for tooltip display
 */
export function processPropertiesForTooltip(
  layerName: string,
  properties: Record<string, any>
): Array<{ key: string; label: string; value: string; order: number }> {
  if (layerName === "apl_pe_observatorio") {
    return processAplPropertiesForTooltip(properties);
  }

  const layerMappings = propertyMappings[layerName];

  // If no mappings exist for this layer, return empty array
  if (!layerMappings) {
    return [];
  }

  const condition = tooltipConditions[layerName];
  if (condition) {
    const blocked = condition.requiredFields.some((field) =>
      isEmptyValue(properties[field])
    );
    if (blocked) return [];
  }

  let correctedProperties =
    layerName === "sitios_arqueologicos"
      ? applySitiosArqueologicosCorrections(properties)
      : properties;

  if (layerName === "gnv_stations") {
    correctedProperties = applyGnvStationDisplayName(correctedProperties);
  }

  if (staticTooltipProperties[layerName]) {
    correctedProperties = {
      ...correctedProperties,
      ...staticTooltipProperties[layerName],
    };
  }

  const processedProperties = Object.entries(correctedProperties)
    .filter(([key, value]) => {
      if (!layerMappings[key]) return false;
      // Mappings flagged allowEmpty rely on format() to supply a fallback for empty values
      if (layerMappings[key].allowEmpty) return true;
      return shouldDisplayProperty(key, value);
    })
    .map(([key, value]) => ({
      key,
      label: getPropertyLabel(layerName, key),
      value: formatPropertyValue(layerName, key, value),
      order: getPropertyOrder(layerName, key),
    }))
    .sort((a, b) => a.order - b.order);

  return processedProperties;
}

// Color luminance calculation constants (based on WCAG 2.0 standard)
const RGB_MAX_VALUE = 255;
const HEX_COLOR_LENGTH = 6;
const HEX_RED_START = 0;
const HEX_GREEN_START = 2;
const HEX_BLUE_START = 4;
const HEX_COMPONENT_LENGTH = 2;

// sRGB gamma correction constants
const SRGB_THRESHOLD = 0.03928;
const SRGB_LOW_DIVISOR = 12.92;
const SRGB_HIGH_OFFSET = 0.055;
const SRGB_HIGH_DIVISOR = 1.055;
const SRGB_GAMMA = 2.4;

// Relative luminance coefficients (ITU-R BT.709)
const LUMINANCE_RED_COEFFICIENT = 0.2126;
const LUMINANCE_GREEN_COEFFICIENT = 0.7152;
const LUMINANCE_BLUE_COEFFICIENT = 0.0722;

// Luminance threshold for determining light vs dark background
const LUMINANCE_THRESHOLD = 0.5;

// Tooltip header styling constants
const TEXT_COLOR_DARK = "#000000";
const TEXT_COLOR_LIGHT = "#ffffff";
const TEXT_SHADOW_LIGHT_BG = "0 1px 2px rgba(0, 0, 0, 0.1)";
const TEXT_SHADOW_DARK_BG = "0 1px 2px rgba(0, 0, 0, 0.3)";
const BORDER_COLOR_LIGHT_BG = "rgba(0, 0, 0, 0.1)";
const BORDER_COLOR_DARK_BG = "rgba(255, 255, 255, 0.2)";
const BORDER_WIDTH = 2;
const TRANSPARENT_COLOR = "transparent";
const LONG_TOOLTIP_VALUE_LENGTH = 80;

function isTransparentColor(color: string): boolean {
  return color.trim().toLowerCase() === TRANSPARENT_COLOR;
}

export function formatDateOnlyString(value: string): string {
  const [year, month, day] = value.split("T")[0].split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function applyGnvStationDisplayName(
  properties: Record<string, any>
): Record<string, any> {
  const displayName = !isEmptyValue(properties.posto_nome_app)
    ? properties.posto_nome_app
    : properties.posto_nome;

  return {
    ...properties,
    posto_nome_app: omitLeadingPosto(displayName),
  };
}

function omitLeadingPosto(value: any): any {
  if (typeof value !== "string") return value;

  const displayName = value.replace(/^posto\b\s*/i, "").trimStart();
  return displayName || value;
}

const formatDateDashToSlash = (value: string): string => {
  if (typeof value !== "string") return value;
  return value.replace(/-/g, "/");
};

/**
 * Calculate relative luminance of a color (0-1 scale)
 * Used to determine if text should be light or dark
 */
function getColorLuminance(color: string): number {
  // Remove # if present and extract hex color
  const hex = color.replace("#", "").substring(0, HEX_COLOR_LENGTH);

  // Convert to RGB (0-1 scale)
  const r =
    parseInt(
      hex.substring(HEX_RED_START, HEX_RED_START + HEX_COMPONENT_LENGTH),
      16
    ) / RGB_MAX_VALUE;
  const g =
    parseInt(
      hex.substring(HEX_GREEN_START, HEX_GREEN_START + HEX_COMPONENT_LENGTH),
      16
    ) / RGB_MAX_VALUE;
  const b =
    parseInt(
      hex.substring(HEX_BLUE_START, HEX_BLUE_START + HEX_COMPONENT_LENGTH),
      16
    ) / RGB_MAX_VALUE;

  // Apply sRGB gamma correction
  const rsRGB =
    r <= SRGB_THRESHOLD
      ? r / SRGB_LOW_DIVISOR
      : Math.pow((r + SRGB_HIGH_OFFSET) / SRGB_HIGH_DIVISOR, SRGB_GAMMA);
  const gsRGB =
    g <= SRGB_THRESHOLD
      ? g / SRGB_LOW_DIVISOR
      : Math.pow((g + SRGB_HIGH_OFFSET) / SRGB_HIGH_DIVISOR, SRGB_GAMMA);
  const bsRGB =
    b <= SRGB_THRESHOLD
      ? b / SRGB_LOW_DIVISOR
      : Math.pow((b + SRGB_HIGH_OFFSET) / SRGB_HIGH_DIVISOR, SRGB_GAMMA);

  // Calculate relative luminance using ITU-R BT.709 coefficients
  return (
    LUMINANCE_RED_COEFFICIENT * rsRGB +
    LUMINANCE_GREEN_COEFFICIENT * gsRGB +
    LUMINANCE_BLUE_COEFFICIENT * bsRGB
  );
}

/**
 * Inline style for the tooltip header, painted with the feature's own color
 * (text color flips to stay readable on light/dark fills).
 */
function buildHeaderStyle(featureColor?: string): string {
  if (!featureColor) return "";

  if (isTransparentColor(featureColor)) {
    return `style="background: ${TRANSPARENT_COLOR}; color: ${TEXT_COLOR_DARK}; text-shadow: ${TEXT_SHADOW_LIGHT_BG}; border-bottom: ${BORDER_WIDTH}px solid ${BORDER_COLOR_LIGHT_BG};"`;
  }

  const luminance = getColorLuminance(featureColor);
  const isLightBackground = luminance > LUMINANCE_THRESHOLD;

  const textColor = isLightBackground ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT;
  const textShadow = isLightBackground
    ? TEXT_SHADOW_LIGHT_BG
    : TEXT_SHADOW_DARK_BG;
  const borderColor = isLightBackground
    ? BORDER_COLOR_LIGHT_BG
    : BORDER_COLOR_DARK_BG;

  return `style="background: ${featureColor}; color: ${textColor}; text-shadow: ${textShadow}; border-bottom: ${BORDER_WIDTH}px solid ${borderColor};"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Tooltip resumido da Vocações Econômicas: só município, RD e os nomes das
 * vocações. O detalhe de cada uma (maturidade, porte, produtos) fica no modal
 * aberto pelo clique — no hover eram até 27 linhas.
 */
function generateVocacaoTooltipHTML(
  properties: Record<string, any>,
  headerStyle: string,
  titleRow: string
): string {
  const vocacoes = parseVocacoes(properties);
  if (vocacoes.length === 0) return "";

  const header = `<div class="tooltip-header" ${headerStyle}>${escapeHtml(
    String(properties.nm_mun ?? "")
  )}</div>`;

  const rd = isEmptyValue(properties.rd)
    ? ""
    : `<div class="tooltip-vocacao-rd">${escapeHtml(
        String(properties.rd)
      )}</div>`;

  const items = vocacoes
    .map(({ nome }) => `<li>${escapeHtml(nome)}</li>`)
    .join("");

  const body = `<div class="tooltip-body">${rd}<ol class="tooltip-vocacao-list">${items}</ol></div>`;
  const footer = `<div class="tooltip-footer">Clique para ver detalhes</div>`;

  return titleRow + header + body + footer;
}

/**
 * Generate HTML content for tooltip with modern structure
 */
export function generateTooltipHTML(
  layerName: string,
  properties: Record<string, any>,
  featureColor?: string,
  layerTitle?: string
): string {
  // Change some spcecific layer titles for better display or updates
  if (layerTitle === "Gasodutos de Distribuição") layerTitle = "";

  // Remove properties that are empty values for subestacoes_expansao_planejada and subestacoes_base_existente
  if (
    layerName === "subestacoes_expansao_planejada" ||
    layerName === "subestacoes_base_existente"
  ) {
    if (
      properties["ano_opera"] &&
      (properties["ano_opera"].toString().trim() === "" ||
        properties["ano_opera"].toString().trim() === "-")
    )
      delete properties["ano_opera"];

    if (
      properties["concession"] &&
      properties["concession"].toString().trim() === ""
    )
      delete properties["concession"];
  }

  // Remove T21:00:00Z from dates of eol_base_existente and ufv_base_existente layers for better display
  if (
    layerName === "eol_base_existente" ||
    layerName === "ufv_base_existente"
  ) {
    if (
      properties["ini_oper"] &&
      properties["ini_oper"].toString().includes("T21:00:00Z")
    ) {
      properties["ini_oper"] = formatDateOnlyString(
        properties["ini_oper"].toString()
      );
    }
  }
  if (layerName === "sitios_arqueologicos") {
    if (
      properties["dt_cadastr"] &&
      properties["dt_cadastr"].toString().includes("-")
    ) {
      properties["dt_cadastr"] = formatDateOnlyString(
        properties["dt_cadastr"].toString()
      );
    }
  }

  // Change dash to slash for unidades_conservacao_ambiental layer for better display
  if (layerName === "unidades_conservacao_ambiental") {
    if (properties["ano_cria6"]) {
      properties["ano_cria6"] = formatDateDashToSlash(properties["ano_cria6"]);
    }
    if (properties["ato_lega9"]) {
      properties["ato_lega9"] = formatDateDashToSlash(properties["ato_lega9"]);
    }
  }

  // Optional layer title shown above the feature name
  const titleRow = layerTitle
    ? `<div class="tooltip-layer-title">${layerTitle}</div>`
    : "";

  if (layerName === VOCACAO_LAYER_NAME) {
    return generateVocacaoTooltipHTML(
      properties,
      buildHeaderStyle(featureColor),
      titleRow
    );
  }

  const processedProperties = processPropertiesForTooltip(
    layerName,
    properties
  );

  if (processedProperties.length === 0) {
    return "";
  }

  // Use first property as header (usually the name)
  const [headerProp, ...bodyProps] = processedProperties;
  const headerStyle = buildHeaderStyle(featureColor);

  // Build structured HTML
  const header = `<div class="tooltip-header" ${headerStyle}>${headerProp.value}</div>`;

  // If there are no body properties, return only the header
  if (bodyProps.length === 0) {
    return titleRow + header;
  }

  // Build body with multiple properties
  const bodyRows = bodyProps
    .map(({ label, value }, index) => {
      const isLongValue = value.length > LONG_TOOLTIP_VALUE_LENGTH;
      const rowClass = isLongValue
        ? "tooltip-row tooltip-row-long"
        : "tooltip-row";
      const valueClass = [
        "tooltip-value",
        index === 0 ? "tooltip-value-primary" : "",
        isLongValue ? "tooltip-value-long" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return `
      <div class="${rowClass}">
        <div class="tooltip-label">${label}</div>
        <div class="${valueClass}">${value}</div>
      </div>
    `;
    })
    .join("");

  const body = `<div class="tooltip-body">${bodyRows}</div>`;

  return titleRow + header + body;
}
